import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk'
import React, { createContext, useContext, useState } from 'react'
import { useUser } from '@paintscout/ui-business'
import type { ReactNode } from 'react'

/**
 * Manual-only flag context
 */
type Flags = Record<string, any>
interface ManualFlagsContextValue {
  flags: Flags
  setFlag: (key: string, value: any) => void
}
const ManualFlagsContext = createContext<ManualFlagsContextValue | undefined>(undefined)

/**
 * Manual provider
 */
interface ManualFlagsProviderProps {
  initialFlags?: Flags
  children: ReactNode
}
export const ManualFlagsProvider = ({ initialFlags = {}, children }: ManualFlagsProviderProps) => {
  const [flags, setFlags] = useState<Flags>(initialFlags)
  const setFlag = (key: string, value: any) => setFlags((prev) => ({ ...prev, [key]: value }))

  return <ManualFlagsContext.Provider value={{ flags, setFlag }}>{children}</ManualFlagsContext.Provider>
}

/**
 * Wrapper hook
 * - If inside ManualFlagsProvider, use manual flags
 * - Otherwise, fallback to LDProvider flags
 */
export const useFlags = (): Flags => {
  const manualContext = useContext(ManualFlagsContext)
  const { nonBilling, isTrial, isCrew, trialExpired, isPainter } = useUser()
  const ldFlags = useLDFlags()

  if (manualContext) return manualContext.flags

  const qualifiesForCRM = (nonBilling && ldFlags.nonBillingCrmGrant) || (isTrial && !trialExpired)
  const shouldAlwaysShowCRM = qualifiesForCRM && !isCrew && ldFlags.crmVisible

  return {
    ...ldFlags,
    crm: shouldAlwaysShowCRM || ldFlags.crm,
    posthogSessionReplay: isPainter || ldFlags.posthogSessionReplay
  }
}
